import clsx from 'clsx';
import { useUnit } from 'effector-react';
import $$ep from 'models/ep';
import { $isGiftTrustPage, $isTrustFundingPage } from 'models/location';
import { $global, storage } from 'models/store-global';
import { Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Frame, H1 } from 'components/StyledTemplates/StyledTemplates';
import AdvisorLogo from 'components/pages/Dashboard/common/AdvisorLogo';
import OrigSpouseTabs from 'components/pages/Dashboard/common/OrigSpouseTabs';

import DashboardService from 'services/DashboardService';

import useExecutionSteps from 'hooks/useExecutionSteps';
import useResources from 'hooks/useResources';

import { MyStore } from 'types/Store';

const DashboardHeader = () => {
    const { t } = useResources();

    const community = useUnit($global.map((state) => state.community));
    const estatePlan = useUnit($$ep.stores.$epQInstance);
    const estatePlanLoaded = useUnit($global.map((state) => state.temp?.estatePlanLoaded || false));
    const userName = useUnit($global.map((state) => state.user?.name || ''));
    const isComplete = estatePlan?.status === 'Complete';
    const isInProgress = estatePlan?.status === 'In_Progress';

    const { tabs: communityTabs = [], realm: participants = [] } = community || {};
    const path = useLocation().pathname.split(`/`).slice(1);
    const isCommunitySectionSelected = path[0] === 'community' && path[1] !== 'assigned';
    const isRolesSectionSelected = path[0] === 'community' && path[1] === 'assigned';
    const isCompleteDashboardSelected = path[0] === 'dashboard' && path[1] === 'complete';
    const isTrustFundingPage = useUnit($isTrustFundingPage);
    const isGiftTrustPage = useUnit($isGiftTrustPage);

    const tabs = participants
        .filter((i: any) => communityTabs.includes(i.participant_id))
        .map((i: any) => ({
            title: `${i.first_name} ${i.last_name}`,
            onClick: () => {
                storage.set(
                    (store): MyStore => ({
                        ...store,
                        temp: {
                            ...(store.temp || {}),
                            selected_community_participant: i.participant_id,
                        },
                    }),
                );
            },
        }));

    /*
        It is required to hide banner in MAP-1707
        https://myadvocateco.atlassian.net/browse/MAP-1707
        Uncomment next line to show banner on non-estate plan screen
    */
    // const bannerIsVisible = isna(estatePlan?.status);
    const bannerIsVisible = !!estatePlan?.status && false;

    const { allDone: remainingStepsAreDone } = useExecutionSteps();

    const handlers = {
        startAssessment: DashboardService.startAssessment,
        printAllEstatePlanDocuments: DashboardService.printAllEstatePlanDocuments,
        requestEstatePlanDelete: DashboardService.requestEstatePlanDelete,
        requestEstatePlanUpdate: DashboardService.requestEstatePlanUpdate,
        requestEstatePlanCancel: DashboardService.requestEstatePlanCancel,
    };

    const getTitle = () => {
        if (isCommunitySectionSelected) {
            if (path[1] === 'advocates') {
                return t('newDashboardPage.menuItemAdvocates');
            }
            if (path[1] === 'beneficiaries') {
                return t('newDashboardPage.menuItemBeneficiaries');
            }
            if (path[1] === 'observers') {
                return t('newDashboardPage.menuItemObservers');
            }
            return t('newDashboardPage.title_community');
        } else if (isRolesSectionSelected) {
            return t('newDashboardPage.title_roles');
        } else if (isTrustFundingPage) {
            return t('newDashboardPage.menuHeaderTrustFunding');
        } else if (isGiftTrustPage) {
            return t('newDashboardPage.menuItemGiftTrust');
        } else {
            return t('newDashboardPage.title_epDocuments');
        }
    };

    return (
        <>
            <Frame extra="position: relative; width: 100%; margin-bottom: 20px;">
                <AdvertisementLine visible={bannerIsVisible}>
                    <span>{t(`newDashboardPage.bannerText`, { name: userName })}</span>
                    <span
                        onClick={() => {
                            handlers.startAssessment();
                        }}
                    >
                        {t('newDashboardPage.bannerLinkText')}
                    </span>
                </AdvertisementLine>
                <Header>
                    <Frame extra="flex-direction: row; @media (max-width: 476px) { width: 100%; justify-content: flex-start; };">
                        <H1
                            extra={`margin: ${
                                tabs.length > 0 && isComplete && !isInProgress ? -20 : 0
                            }px 25px 0 0px !important; font-size: 35px !important;`}
                        >
                            {estatePlanLoaded ? getTitle() : <Spinner animation="border" />}
                        </H1>
                    </Frame>

                    {!!estatePlan?.status && <AdvisorLogo />}
                </Header>
                <Frame
                    extra="width: 100%; align-items: flex-start;"
                    className={clsx({
                        'd-none':
                            path[1] === 'in-progress' ||
                            (isCompleteDashboardSelected && !remainingStepsAreDone) ||
                            isTrustFundingPage ||
                            isGiftTrustPage,
                    })}
                >
                    <OrigSpouseTabs />
                </Frame>
            </Frame>
            {bannerIsVisible && <Frame extra="height: 45px;" />}
        </>
    );
};

const AdvertisementLine = styled(Frame)<{ extra?: string; visible?: boolean }>`
    width: 100vw;
    height: 45px;
    background: ${({ theme }) => theme.permission};
    flex-direction: row;

    /*font-family: Open Sans;*/
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 30px;
    text-align: center;
    color: #ffffff;

    display: ${({ visible }) => (visible ? 'flex' : 'none')};

    > * {
        &:nth-child(2) {
            text-decoration: underline;
            margin-left: 5px;
            cursor: pointer;
        }
    }
`;

export const Tab = styled(Frame)<{ onClick?: any; selected?: any; tabsNumber?: number }>`
    padding: 7px 20px;
    /* background: ${(props) => (props.selected ? props.theme.background.secondary : `transparent`)}; */
    /* color: ${(props) => (props.selected ? props.theme.text.secondary : `white`)}; */
    color: ${(props) => props.theme.text.secondary};
    /* border-radius: 5px 5px 0px 0px; */
    margin-right: 2px;
    /*font-family: Open Sans;*/
    font-style: normal;
    font-weight: 600;
    font-size: 17px;
    line-height: 30px;
    cursor: pointer;

    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: calc(1200px / ${({ tabsNumber = 1 }: any) => tabsNumber});

    border-bottom: 2px solid ${({ theme, selected = false }: any) => (selected ? theme.primary_action : `transparent`)};

    @media (max-width: 476px) {
        font-size: 14px;
        padding: 7px 10px;
    }
`;

export const TabsWrapper = styled(Frame)`
    flex-direction: row;
    margin-left: 15px;
    justify-content: flex-start;

    @media (max-width: 476px) {
        margin-left: 5px;
    }
`;

const Header = styled(Frame)`
    width: 100%;
    /* height: 120px; */
    /* padding: 0 calc((100vw - 1200px) / 2); */
    box-sizing: border-box;
    /* background: ${(props) => props.theme.primary_action}; */
    margin-top: 45px;
    flex-direction: row;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);

    > * {
        flex-direction: row;
    }

    @media (max-width: 476px) {
        flex-direction: column;
        gap: 10px;
    }
`;

export default DashboardHeader;
